import React, { useEffect, useState } from "react";

import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../firebase";

import "./AdminUsdtTransaction.css";


const AdminUsdtTransaction = () => {

  // =====================================================
  // STATE
  // =====================================================

  const [transactions, setTransactions] =
    useState([]);

  const [allTransactions, setAllTransactions] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [updatingId, setUpdatingId] =
    useState(null);


  // =====================================================
  // LOAD USDT + TSH TRANSACTIONS
  // =====================================================

  useEffect(() => {

    let usdtTransactions = [];
    let tshTransactions = [];

    let usdtLoaded = false;
    let tshLoaded = false;


    // ===================================================
    // PROCESS
    // ===================================================

    const processTransactions = () => {

      if (
        !usdtLoaded ||
        !tshLoaded
      ) {
        return;
      }


      const combined = [

        ...usdtTransactions.map(
          (tx) => ({
            ...tx,
            collection:
              "usdt_transactions",
          })
        ),

        ...tshTransactions.map(
          (tx) => ({
            ...tx,
            collection:
              "tsh_transactions",
          })
        ),

      ];


      // =================================================
      // SORT OLDEST FIRST
      // =================================================

      const sorted =
        [...combined].sort(
          (a, b) => {

            const aTime =
              a.createdAt?.seconds ||
              a.timestamp?.seconds ||
              0;

            const bTime =
              b.createdAt?.seconds ||
              b.timestamp?.seconds ||
              0;

            return aTime - bTime;

          }
        );


      // =================================================
      // CALCULATE POSITION
      //
      // ONLY SUCCESS TRANSACTIONS COUNT
      //
      // first
      // second
      // other
      // =================================================

      const successfulPerUser = {};


      const transactionsWithPosition =
        sorted.map((tx) => {

          const username =
            tx.username;


          if (!username) {

            return {
              ...tx,
              position:
                tx.position ||
                "other",
            };

          }


          // =============================================
          // EXISTING POSITION
          // =============================================

          if (
            tx.status ===
            "success"
          ) {

            if (
              successfulPerUser[
                username
              ] === undefined
            ) {

              successfulPerUser[
                username
              ] = 0;

            }


            successfulPerUser[
              username
            ] += 1;


            let position =
              tx.position;


            if (
              !position
            ) {

              if (
                successfulPerUser[
                  username
                ] === 1
              ) {

                position =
                  "first";

              }

              else if (
                successfulPerUser[
                  username
                ] === 2
              ) {

                position =
                  "second";

              }

              else {

                position =
                  "other";

              }

            }


            return {
              ...tx,
              position,
            };

          }


          // =============================================
          // PENDING / PROCESSING / REJECTED
          // =============================================

          return {
            ...tx,
            position:
              tx.position ||
              "other",
          };

        });


      // =================================================
      // SET ALL
      // =================================================

      setAllTransactions(
        transactionsWithPosition
      );


      // =================================================
      // USDT ONLY
      // =================================================

      const usdtOnly =
        transactionsWithPosition
          .filter(
            (tx) =>
              tx.collection ===
              "usdt_transactions"
          )
          .sort(
            (a, b) => {

              const aTime =
                a.createdAt?.seconds ||
                a.timestamp?.seconds ||
                0;

              const bTime =
                b.createdAt?.seconds ||
                b.timestamp?.seconds ||
                0;

              return bTime - aTime;

            }
          );


      setTransactions(
        usdtOnly
      );


      setLoading(false);

    };


    // ===================================================
    // USDT REALTIME
    // ===================================================

    const unsubscribeUsdt =
      onSnapshot(

        collection(
          db,
          "usdt_transactions"
        ),

        (snapshot) => {

          usdtTransactions =
            snapshot.docs.map(
              (item) => ({

                id:
                  item.id,

                ...item.data(),

              })
            );


          usdtLoaded =
            true;


          processTransactions();

        },

        (error) => {

          console.error(
            "Failed to load USDT transactions:",
            error
          );


          usdtLoaded =
            true;


          processTransactions();

        }

      );


    // ===================================================
    // TSH REALTIME
    //
    // Hii inatumika kusaidia position
    // isiwe duplicated kati ya TSH na USDT.
    // ===================================================

    const unsubscribeTsh =
      onSnapshot(

        collection(
          db,
          "tsh_transactions"
        ),

        (snapshot) => {

          tshTransactions =
            snapshot.docs.map(
              (item) => ({

                id:
                  item.id,

                ...item.data(),

              })
            );


          tshLoaded =
            true;


          processTransactions();

        },

        (error) => {

          console.error(
            "Failed to load TSH transactions:",
            error
          );


          tshLoaded =
            true;


          processTransactions();

        }

      );


    // ===================================================
    // CLEANUP
    // ===================================================

    return () => {

      unsubscribeUsdt();

      unsubscribeTsh();

    };

  }, []);


  // =====================================================
  // UPDATE STATUS
  // =====================================================

  const updateStatus = async (
    transaction,
    newStatus
  ) => {

    const transactionId =
      transaction.id;


    setUpdatingId(
      transactionId
    );


    try {

      const previousStatus =
        String(
          transaction.status ||
          "pending"
        ).toLowerCase();


      const normalizedNewStatus =
        String(
          newStatus
        ).toLowerCase();


      // =================================================
      // SAME STATUS
      // =================================================

      if (
        previousStatus ===
        normalizedNewStatus
      ) {

        return;

      }


      // =================================================
      // USERNAME VALIDATION
      // =================================================

      if (
        !transaction.username
      ) {

        alert(
          "Transaction has no username."
        );

        return;

      }


      // =================================================
      // PREVENT DOUBLE SUCCESS
      // =================================================

      if (
        previousStatus ===
        "success" &&
        normalizedNewStatus ===
        "success"
      ) {

        return;

      }


      // =================================================
      // TRANSACTION REF
      // =================================================

      const transactionRef =
        doc(
          db,
          "usdt_transactions",
          transactionId
        );


      // =================================================
      // SUCCESS PROCESSING
      // =================================================

      if (
        normalizedNewStatus ===
        "success"
      ) {

        // =============================================
        // ONLY CREDIT BALANCE IF PREVIOUS WAS NOT SUCCESS
        // =============================================

        const shouldCreditBalance =
          previousStatus !==
          "success";


        // =============================================
        // FIND USER
        // =============================================

        const usersRef =
          collection(
            db,
            "users"
          );


        const userQuery =
          query(
            usersRef,
            where(
              "username",
              "==",
              transaction.username
            )
          );


        const userSnapshot =
          await getDocs(
            userQuery
          );


        if (
          userSnapshot.empty
        ) {

          alert(
            "User account was not found."
          );

          return;

        }


        const userDoc =
          userSnapshot.docs[0];


        const userData =
          userDoc.data();


        // =============================================
        // AMOUNT
        // =============================================

        const transactionAmount =
          Number(
            transaction.amount || 0
          );


        if (
          !Number.isFinite(
            transactionAmount
          ) ||
          transactionAmount <= 0
        ) {

          alert(
            "Invalid transaction amount."
          );

          return;

        }


        // =============================================
        // POSITION
        //
        // Look at ALL successful transactions
        // for this username.
        // =============================================

        const successfulTransactions =
          allTransactions
            .filter(
              (tx) =>
                tx.username ===
                  transaction.username &&

                tx.status ===
                  "success" &&

                !(
                  tx.id ===
                    transaction.id &&

                  tx.collection ===
                    "usdt_transactions"
                )
            )
            .sort(
              (a, b) => {

                const aTime =
                  a.createdAt?.seconds ||
                  a.timestamp?.seconds ||
                  0;

                const bTime =
                  b.createdAt?.seconds ||
                  b.timestamp?.seconds ||
                  0;

                return (
                  aTime -
                  bTime
                );

              }
            );


        let newPosition =
          "other";


        if (
          successfulTransactions.length ===
          0
        ) {

          newPosition =
            "first";

        }

        else if (
          successfulTransactions.length ===
          1
        ) {

          newPosition =
            "second";

        }

        else {

          newPosition =
            "other";

        }


        // =============================================
        // UPDATE TRANSACTION
        // =============================================

        await updateDoc(
          transactionRef,
          {
            status:
              "success",

            position:
              newPosition,
          }
        );


        // =============================================
        // UPDATE BALANCE
        //
        // IMPORTANT:
        // ONLY ONCE
        // =============================================

        if (
          shouldCreditBalance
        ) {

          const currentUsdtBalance =
            Number(
              userData.usdtBalance || 0
            );


          const newUsdtBalance =
            currentUsdtBalance +
            transactionAmount;


          await updateDoc(
            doc(
              db,
              "users",
              userDoc.id
            ),
            {
              usdtBalance:
                newUsdtBalance,
            }
          );


          console.log(
            `✅ ${transaction.username} USDT balance updated`
          );


          console.log(
            `Previous balance: ${currentUsdtBalance}`
          );


          console.log(
            `Amount: ${transactionAmount}`
          );


          console.log(
            `New balance: ${newUsdtBalance}`
          );

        }


        alert(
          `Success! ${transactionAmount} USDT added to ${transaction.username}.\nPosition: ${newPosition}`
        );


        return;

      }


      // =================================================
      // REJECTED / PROCESSING / PENDING / EXPIRED
      // =================================================

      await updateDoc(
        transactionRef,
        {
          status:
            newStatus,
        }
      );


    }

    catch (error) {

      console.error(
        "Status update failed:",
        error
      );


      alert(
        "Status update failed. Check console."
      );

    }

    finally {

      setUpdatingId(
        null
      );

    }

  };


  // =====================================================
  // FORMAT DATE
  // =====================================================

  const formatDate = (
    timestamp
  ) => {

    if (
      !timestamp
    ) {

      return "-";

    }


    try {

      if (
        typeof timestamp.toDate ===
        "function"
      ) {

        return timestamp
          .toDate()
          .toLocaleString();

      }


      if (
        timestamp.seconds
      ) {

        return new Date(
          timestamp.seconds *
            1000
        ).toLocaleString();

      }


      return "-";

    }

    catch {

      return "-";

    }

  };


  // =====================================================
  // POSITION COLOR
  // =====================================================

  const getPositionClass = (
    position
  ) => {

    if (
      position ===
      "first"
    ) {

      return "position-first";

    }


    if (
      position ===
      "second"
    ) {

      return "position-second";

    }


    return "position-other";

  };


  // =====================================================
  // RENDER
  // =====================================================

  return (

    <div
      className="admin-container"
    >

      <h2>
        USDT Transactions
      </h2>


      {/* =================================================
          SUMMARY
      ================================================= */}

      <div
        style={{
          display: "flex",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "20px",
        }}
      >

        <div
          style={{
            padding: "12px 18px",
            background: "#111",
            borderRadius: "10px",
            color: "#fff",
          }}
        >

          <strong>
            Total USDT:
          </strong>{" "}

          {transactions.length}

        </div>


        <div
          style={{
            padding: "12px 18px",
            background: "#111",
            borderRadius: "10px",
            color: "#00ff99",
          }}
        >

          <strong>
            Success:
          </strong>{" "}

          {
            transactions.filter(
              (tx) =>
                tx.status ===
                "success"
            ).length
          }

        </div>


        <div
          style={{
            padding: "12px 18px",
            background: "#111",
            borderRadius: "10px",
            color: "#ffd700",
          }}
        >

          <strong>
            First:
          </strong>{" "}

          {
            transactions.filter(
              (tx) =>
                tx.status ===
                  "success" &&
                tx.position ===
                  "first"
            ).length
          }

        </div>


        <div
          style={{
            padding: "12px 18px",
            background: "#111",
            borderRadius: "10px",
            color: "#00bfff",
          }}
        >

          <strong>
            Second:
          </strong>{" "}

          {
            transactions.filter(
              (tx) =>
                tx.status ===
                  "success" &&
                tx.position ===
                  "second"
            ).length
          }

        </div>


        <div
          style={{
            padding: "12px 18px",
            background: "#111",
            borderRadius: "10px",
            color: "#aaa",
          }}
        >

          <strong>
            Other:
          </strong>{" "}

          {
            transactions.filter(
              (tx) =>
                tx.status ===
                  "success" &&
                tx.position ===
                  "other"
            ).length
          }

        </div>

      </div>


      {/* =================================================
          LOADING
      ================================================= */}

      {loading ? (

        <p>
          Inapakia...
        </p>

      ) : (

        <div
          className="table-wrapper"
        >

          <table
            className="deposit-table"
          >

            <thead>

              <tr>

                <th>
                  Position
                </th>

                <th>
                  Username
                </th>

                <th>
                  Coin
                </th>

                <th>
                  Network
                </th>

                <th>
                  Address
                </th>

                <th>
                  Amount
                </th>

                <th>
                  Date
                </th>

                <th>
                  Status
                </th>

              </tr>

            </thead>


            <tbody>

              {transactions.length ===
              0 ? (

                <tr>

                  <td
                    colSpan="8"
                    style={{
                      textAlign:
                        "center",
                    }}
                  >

                    No USDT transactions
                    found.

                  </td>

                </tr>

              ) : (

                transactions.map(
                  (transaction) => (

                    <tr
                      key={
                        transaction.id
                      }
                    >

                      {/* =================================
                          POSITION
                      ================================= */}

                      <td>

                        <strong
                          className={
                            getPositionClass(
                              transaction.position
                            )
                          }
                        >

                          {
                            transaction.position ||
                            "other"
                          }

                        </strong>

                      </td>


                      {/* =================================
                          USERNAME
                      ================================= */}

                      <td>

                        {
                          transaction.username ||
                          "-"
                        }

                      </td>


                      {/* =================================
                          COIN
                      ================================= */}

                      <td>

                        {
                          transaction.coin ||
                          "USDT"
                        }

                      </td>


                      {/* =================================
                          NETWORK
                      ================================= */}

                      <td>

                        {
                          transaction.network ||
                          "-"
                        }

                      </td>


                      {/* =================================
                          ADDRESS
                      ================================= */}

                      <td
                        className="transaction-address"
                      >

                        {
                          transaction.address ||
                          transaction.userUSDTAddress ||
                          "-"
                        }

                      </td>


                      {/* =================================
                          AMOUNT
                      ================================= */}

                      <td>

                        <strong>

                          {
                            Number(
                              transaction.amount ||
                                0
                            ).toLocaleString()
                          }

                        </strong>{" "}

                        USDT

                      </td>


                      {/* =================================
                          DATE
                      ================================= */}

                      <td>

                        {
                          formatDate(
                            transaction.createdAt ||
                            transaction.timestamp
                          )
                        }

                      </td>


                      {/* =================================
                          STATUS
                      ================================= */}

                      <td>

                        <select
                          disabled={
                            updatingId ===
                            transaction.id
                          }
                          value={
                            transaction.status ||
                            "pending"
                          }
                          onChange={
                            (e) =>
                              updateStatus(
                                transaction,
                                e.target.value
                              )
                          }
                          className={
                            `status-select ${
                              transaction.status ||
                              "pending"
                            }`
                          }
                        >

                          <option value="pending">
                            Pending
                          </option>

                          <option value="processing">
                            Processing
                          </option>

                          <option value="success">
                            Success
                          </option>

                          <option value="rejected">
                            Rejected
                          </option>

                          <option value="Expired">
                            Expired
                          </option>

                        </select>

                      </td>

                    </tr>

                  )
                )

              )}

            </tbody>

          </table>

        </div>

      )}

    </div>

  );

};


export default AdminUsdtTransaction;
